/**
 * Product Form — Handles variant selection, Section Rendering API
 * updates, and form submission for the product page.
 *
 * Extends the Component base class. Listens for radio input changes
 * on variant option groups, fetches re-rendered section HTML from
 * the server, and surgically updates price, button state, images,
 * availability, and URL.
 *
 * Usage (in Liquid):
 *   <product-form data-unavailable-text="{{ 'products.product.unavailable' | t }}">
 *     {% content_for 'blocks' %}
 *   </product-form>
 */

import { Component } from '@theme/component';
import { cartMutations } from '@theme/cart-mutations';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';

class ProductForm extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.select = this.querySelector('select[name="id"]');
    this.submitBtn = this.querySelector('button[type="submit"]');
    this.submitLabel = this.submitBtn?.querySelector('[data-btn-label]');
    this.priceEl = this.querySelector('[data-product-price]');
    this.statusEl = this.querySelector('[data-variant-status]');
    this.unavailableText = this.dataset.unavailableText;
    this.addedText = this.dataset.addedText || 'Added';
    this.abortController = null;
    this._addedTimeout = null;
    this._errorTimeout = null;
    this._originalLabel = null;

    this.addEventListener('change', this._onChange.bind(this));

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this._onSubmit.bind(this));
    }

    this.bindZoomClicks();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.abortController) this.abortController.abort();
    if (this._addedTimeout) clearTimeout(this._addedTimeout);
    if (this._errorTimeout) clearTimeout(this._errorTimeout);
    this.unbindZoomClicks();
  }

  _onChange(e) {
    const input = e.target.closest('[data-option-value]');
    if (!input) return;

    // Update displayed value in the fieldset header
    const group = input.closest('[data-slot="option-group"]');
    const valueEl = group?.querySelector('[data-slot="fieldset-value"]');
    if (valueEl) valueEl.textContent = input.dataset.optionValue;

    // Dispatch event for other components
    document.dispatchEvent(new VariantSelectedEvent({ input, group }));

    this.fetchSection();
  }

  async _onSubmit(e) {
    e.preventDefault();

    const form = e.target;
    if (this.select && !this.select.value) return;

    // Clear any previous error
    this._clearError();

    // Disable button while submitting
    if (this.submitBtn) this.submitBtn.disabled = true;

    const formData = new FormData(form);
    const item = {
      id: parseInt(formData.get('id'), 10),
      quantity: parseInt(formData.get('quantity') || '1', 10),
    };

    // Collect line item properties (gift card recipient fields, etc.)
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') {
        properties[match[1]] = value;
      }
    }
    if (Object.keys(properties).length) item.properties = properties;

    try {
      // The queue re-renders the cart drawer and dispatches CartUpdateEvent.
      const data = await cartMutations.add({ items: [item] }, 'product-form');

      // Dispatch cart add event
      document.dispatchEvent(new CartAddEvent(data, this.sectionId));

      // Show "Added" feedback on the button
      this._showAddedFeedback();

      // Open the cart drawer
      const drawerDialog = document.querySelector('dialog-component:has(#cart-drawer)');
      if (drawerDialog?.showDialog) drawerDialog.showDialog();
    } catch (err) {
      console.error('Add to cart failed:', err);
      document.dispatchEvent(new CartErrorEvent(err.message, err.description, err.errors));
      this._showError(err.message);
      if (this.submitBtn) this.submitBtn.disabled = false;
    }
  }

  _showAddedFeedback() {
    if (!this.submitBtn || !this.submitLabel) return;
    this._originalLabel = this.submitLabel.textContent;
    this.submitBtn.setAttribute('data-added', 'true');
    this.submitLabel.textContent = this.addedText;
    this.submitBtn.disabled = false;

    if (this._addedTimeout) clearTimeout(this._addedTimeout);
    this._addedTimeout = setTimeout(() => {
      this.submitBtn.removeAttribute('data-added');
      this.submitLabel.textContent = this._originalLabel;
      this._addedTimeout = null;
    }, 1500);
  }

  _showError(message) {
    const errorEl = this.querySelector('[data-cart-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.removeAttribute('hidden');
    errorEl.setAttribute('role', 'alert');

    if (this._errorTimeout) clearTimeout(this._errorTimeout);
    this._errorTimeout = setTimeout(() => {
      this._clearError();
    }, 10000);
  }

  _clearError() {
    const errorEl = this.querySelector('[data-cart-error]');
    if (!errorEl) return;
    errorEl.setAttribute('hidden', '');
    errorEl.removeAttribute('role');
    errorEl.textContent = '';
    if (this._errorTimeout) {
      clearTimeout(this._errorTimeout);
      this._errorTimeout = null;
    }
  }

  fetchSection() {
    if (!this.sectionId || !this.select) return;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const previousVariantId = this.select.value;
    this.select.value = '';

    // Collect selected option value IDs
    const ids = [];
    const groups = this.querySelectorAll('[data-slot="option-group"]');
    for (const group of groups) {
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked?.dataset.optionValueId) {
        ids.push(checked.dataset.optionValueId);
      }
    }
    if (ids.length === 0) return;

    const url =
      window.location.pathname +
      '?section_id=' + encodeURIComponent(this.sectionId) +
      '&option_values=' + ids.join(',');

    const sentIds = ids;

    fetch(url, { signal: this.abortController.signal })
      .then((res) => res.text())
      .then((html) => {
        this.syncSection(html, sentIds);

        // Dispatch update event for other components
        document.dispatchEvent(new VariantUpdateEvent({
          html,
          sectionId: this.sectionId,
        }));
      })
      .catch((e) => {
        if (e.name !== 'AbortError') {
          if (this.select) this.select.value = previousVariantId;
          if (this.submitBtn) this.submitBtn.disabled = false;
        }
      });
  }

  syncSection(html, sentIds) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const isExactMatch = this.verifyOptionMatch(doc, sentIds);

    // Anchor scroll position — iOS Safari shifts on DOM updates
    const anchorBefore = this.getBoundingClientRect().top;

    // Variant ID and URL
    const newSelect = doc.querySelector('select[name="id"]');
    const selectedOption = newSelect?.querySelector('option[selected]');
    const url = new URL(window.location.href);

    if (isExactMatch && selectedOption) {
      if (this.select) this.select.value = selectedOption.value;
      url.searchParams.set('variant', selectedOption.value);
    } else {
      if (this.select) this.select.value = '';
      url.searchParams.delete('variant');
    }
    history.replaceState({}, '', url.toString());

    // Button state
    if (isExactMatch) {
      const newBtn = doc.querySelector('button[type="submit"]');
      if (this.submitBtn && newBtn) {
        this.submitBtn.disabled = newBtn.disabled;
        const newLabel = newBtn.querySelector('[data-btn-label]');
        if (this.submitLabel && newLabel) {
          this.submitLabel.textContent = newLabel.textContent;
        }
      }
    } else {
      if (this.submitBtn) this.submitBtn.disabled = true;
      if (this.submitLabel) {
        this.submitLabel.textContent = this.unavailableText || 'Unavailable';
      }
    }

    // Price
    if (this.priceEl) {
      const newPrice = doc.querySelector('[data-product-price]');
      if (isExactMatch && newPrice) {
        this.priceEl.innerHTML = newPrice.innerHTML;
      } else if (!isExactMatch) {
        this.priceEl.innerHTML = '';
      }
    }

    // Image grid (exact match only)
    if (isExactMatch) {
      const productEl = this.closest('.product');
      const mediaContainer = productEl?.querySelector('[data-product-media]');
      const newMedia = doc.querySelector('[data-product-media]');
      if (mediaContainer && newMedia) {
        mediaContainer.innerHTML = newMedia.innerHTML;
      }

      // Zoom dialog content
      const zoomEl = productEl?.querySelector('media-zoom');
      const newZoomContent = doc.querySelector('[data-media-zoom-content]');
      if (zoomEl && newZoomContent) {
        const wasOpen = zoomEl.dialog?.open;

        if (wasOpen) {
          const newZoomEl = newZoomContent.querySelector('media-zoom');
          if (newZoomEl) {
            const pairs = [
              ['.media-zoom__thumbnails', '.media-zoom__thumbnails'],
              ['.media-zoom__images', '.media-zoom__images'],
              ['[data-media-zoom-colors]', '[data-media-zoom-colors]'],
            ];
            for (const [curSel, newSel] of pairs) {
              const cur = zoomEl.querySelector(curSel);
              const nw = newZoomEl.querySelector(newSel);
              if (cur && nw) cur.innerHTML = nw.innerHTML;
            }
            zoomEl.reinit();
          }
        } else {
          const zoomWrapper = productEl.querySelector('[data-media-zoom-content]');
          if (zoomWrapper) zoomWrapper.innerHTML = newZoomContent.innerHTML;
        }
      }
    }

    // Correct scroll drift from DOM updates
    const drift = this.getBoundingClientRect().top - anchorBefore;
    if (drift) window.scrollBy(0, drift);

    // Option availability
    const currentGroups = this.querySelectorAll('[data-slot="option-group"]');
    const newGroups = doc.querySelectorAll('[data-slot="option-group"]');

    for (let g = 0; g < currentGroups.length && g < newGroups.length; g++) {
      const currentOptions = currentGroups[g].querySelectorAll('[data-option-available]');
      const newOptions = newGroups[g].querySelectorAll('[data-option-available]');

      for (let i = 0; i < currentOptions.length && i < newOptions.length; i++) {
        const avail = newOptions[i].getAttribute('data-option-available');
        currentOptions[i].setAttribute('data-option-available', avail);

        const input = currentOptions[i].querySelector('input[type="radio"]');
        const newInput = newOptions[i].querySelector('input[type="radio"]');
        if (input) {
          if (avail === 'false') {
            const desc = newInput?.getAttribute('aria-description') || '';
            if (desc) input.setAttribute('aria-description', desc);
          } else {
            input.removeAttribute('aria-description');
          }
        }
      }
    }

    // Accessibility announcement
    this.announceStatus(isExactMatch, selectedOption);

    // Rebind zoom clicks after media swap
    if (isExactMatch) this.bindZoomClicks();
  }

  verifyOptionMatch(doc, sentIds) {
    const responseIds = [];
    const groups = doc.querySelectorAll('[data-slot="option-group"]');
    for (const group of groups) {
      const checked = group.querySelector('input[type="radio"][checked]');
      if (checked?.dataset.optionValueId) {
        responseIds.push(checked.dataset.optionValueId);
      }
    }
    if (sentIds.length !== responseIds.length) return false;
    return sentIds.every((id, i) => id === responseIds[i]);
  }

  announceStatus(isExactMatch, selectedOption) {
    if (!this.statusEl) return;
    const parts = [];
    if (isExactMatch && selectedOption) {
      parts.push(selectedOption.textContent.trim());
    }
    if (this.submitLabel) parts.push(this.submitLabel.textContent.trim());

    this.statusEl.textContent = '';
    requestAnimationFrame(() => {
      this.statusEl.textContent = parts.join('. ');
    });
  }

  bindZoomClicks() {
    this.unbindZoomClicks();
    const productEl = this.closest('.product');
    const media = productEl?.querySelector('[data-product-media]');
    if (!media) return;

    const zoomDialogId = media.dataset.zoomDialog;
    if (!zoomDialogId) return;

    const images = media.querySelectorAll('.product__image');
    const zoomLabel = media.dataset.carouselLabel || 'Zoom image';
    for (const img of images) {
      img.setAttribute('tabindex', '0');
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', zoomLabel);
    }

    const openZoom = (imageWrap) => {
      const allImages = Array.from(media.querySelectorAll('.product__image'));
      const index = Math.max(0, allImages.indexOf(imageWrap));
      const zoomEl = productEl.querySelector('media-zoom#' + zoomDialogId);
      if (zoomEl?.openToIndex) zoomEl.openToIndex(index, imageWrap);
    };

    this._zoomClickHandler = (e) => {
      const imageWrap = e.target.closest('.product__image');
      if (imageWrap) openZoom(imageWrap);
    };

    this._zoomKeyHandler = (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const imageWrap = e.target.closest('.product__image');
      if (!imageWrap) return;
      e.preventDefault();
      openZoom(imageWrap);
    };

    media.addEventListener('click', this._zoomClickHandler);
    media.addEventListener('keydown', this._zoomKeyHandler);

    // "View all" button opens zoom to first image
    const viewAllBtn = productEl.querySelector('[data-open-zoom]');
    if (viewAllBtn) {
      this._viewAllHandler = () => {
        const index = parseInt(viewAllBtn.dataset.openZoomIndex, 10) || 0;
        const zoomEl = productEl.querySelector('media-zoom#' + zoomDialogId);
        if (zoomEl?.openToIndex) zoomEl.openToIndex(index, null);
      };
      viewAllBtn.addEventListener('click', this._viewAllHandler);
    }
  }

  unbindZoomClicks() {
    const productEl = this.closest('.product');
    const media = productEl?.querySelector('[data-product-media]');
    if (media) {
      if (this._zoomClickHandler) media.removeEventListener('click', this._zoomClickHandler);
      if (this._zoomKeyHandler) media.removeEventListener('keydown', this._zoomKeyHandler);
    }
    this._zoomClickHandler = null;
    this._zoomKeyHandler = null;
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
